import { DatePipe } from '@angular/common';
import { Component, Input } from '@angular/core';

import { TranslateService } from '@ngx-translate/core';
import { Observable, of } from 'rxjs';
import { switchMap } from 'rxjs/operators';

import { NotificationType } from '@stylehaven/components';
import { ReasonCode } from '../../../entities/common/reason-code.enum';
import { SubjectRejection } from '../../../entities/common/subject-rejection.entity';
import { RejectionTranslations } from '../../entities/rejection-translations.entity';

@Component({
    standalone: false,
    selector: 'digihaven-rejection-notification',
    templateUrl: './rejection-notification.component.html',
    styleUrls: ['./rejection-notification.component.scss']
})
export class RejectionNotificationComponent {
    notificationType: typeof NotificationType = NotificationType;

    @Input() rejection!: SubjectRejection;
    @Input() rejectionTranslations!: RejectionTranslations;

    constructor(
        private readonly translateService: TranslateService,
        private readonly datePipe: DatePipe
    ) {}

    getRejectedBy(): Observable<string> {
        if (this.rejection) {
            const rejectionDate: string | null = this.datePipe.transform(this.rejection.Time, 'dd MMM yyyy HH:mm');

            return this.rejectionBySystem()
                ? this.getRejectedBySystem(rejectionDate)
                : this.getRejectedByUser(rejectionDate, this.rejection.User);
        } else {
            return of('');
        }
    }

    getRejectedInfo(): Observable<string> {
        if (this.rejectionBySystem() && this.isDocumentReason()) {
            return this.translateService.get('representations.notifications.rejected');
        } else {
            return of('');
        }
    }

    private rejectionBySystem() {
        return this.rejection && this.rejection.User === 'SYSTEM';
    }

    isDocumentReason() {
        return (
            this.rejection.SystemCode == ReasonCode.FORMAT_BLACKLISTED ||
            this.rejection.SystemCode == ReasonCode.FORMAT_UNKNOWN ||
            this.rejection.SystemCode == ReasonCode.VIRUS ||
            this.rejection.SystemCode == ReasonCode.MD5
        );
    }

    getMotivation(): string | undefined {
        if (this.rejection.Motivation) {
            if (this.rejectionBySystem()) {
                return `${this.translateService.instant(this.rejectionTranslations.technicalMotivationTranslationKey)}: ${this.rejection.Motivation}`;
            } else {
                return `${this.rejection.Motivation}`;
            }
        } else {
            return '';
        }
    }

    getSystemCodeTranslation(): Observable<string> {
        return this.rejection.SystemCode
            ? this.translateService.get(
                  `${this.rejectionTranslations.systemCodeTranslationKey}.${this.rejection.SystemCode}`
              )
            : of('');
    }

    private getRejectedBySystem(date: string | null): Observable<string> {
        return this.getTechnicalCheckTranslation().pipe(
            switchMap(technicalCheckTranslation => this.getRejectedByTranslation(date, technicalCheckTranslation))
        );
    }

    private getRejectedByUser(date: string | null, user: string): Observable<string> {
        return this.translateService.get(this.rejectionTranslations.rejectedByTranslationKey, {
            date,
            user
        });
    }

    private getTechnicalCheckTranslation(): Observable<string> {
        return this.translateService.get(this.rejectionTranslations.technicalCheckTranslationKey);
    }

    private getRejectedByTranslation(date: string | null, user: string): Observable<string> {
        return this.translateService.get(this.rejectionTranslations.rejectedByTranslationKey, {
            date,
            user
        });
    }
}
